import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { SitePages, Pages } from "@/lib/api";
import EditableHTML from "@/components/EditableHTML";
import ReflectedHeading from "@/components/ReflectedHeading";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import VideoModal from "@/components/VideoModal";
import { ArrowLeft } from "lucide-react";

const CustomPage = () => {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [homeContent, setHomeContent] = useState({});

  useEffect(() => {
    setPage(null);
    setNotFound(false);
    (async () => {
      try {
        setPage(await SitePages.getPublic(slug));
      } catch (err) {
        // Only mark as truly not-found on an explicit HTTP 404.
        // Network errors, 5xx, CORS blips, timeouts, etc. must NOT trigger
        // a noindex — Google would cache that verdict and drop a valid page.
        if (err?.response?.status === 404) {
          setNotFound(true);
        }
      }
      try { setHomeContent((await Pages.get("home")).sections || {}); } catch {}
    })();
  }, [slug]);

  if (notFound) {
    return (
      <main className="min-h-screen pt-40 px-6 text-center text-slate-200" data-testid="custom-page-not-found">
        <SEO title="Page not found" noindex path={`/p/${slug}`} />
        <div className="text-3xl font-display font-bold">Page not found</div>
        <Link to="/" className="text-atc-blue mt-4 inline-block">← Back home</Link>
      </main>
    );
  }
  if (!page) return <div className="min-h-screen flex items-center justify-center text-slate-400">Loading…</div>;

  // Parse custom schema JSON if valid, otherwise omit
  let pageSchema;
  if (page.seo_schema_json) {
    try { pageSchema = JSON.parse(page.seo_schema_json); }
    catch { pageSchema = undefined; }
  }

  const hasHero = page.hero_title || page.hero_image_url;

  return (
    <main className="min-h-screen text-slate-100 pt-32" data-testid="custom-page">
      <SEO
        title={page.title}
        description={page.meta_description || `${page.title} — ATC Mold & Water Restoration`}
        path={`/p/${page.slug}`}
        image={page.hero_image_url || undefined}
        breadcrumbs={[{ name: page.title, path: `/p/${page.slug}` }]}
        schema={pageSchema}
      />
      {page.intro_video_url && (
        <VideoModal
          url={page.intro_video_url}
          title={`${page.title} — intro video`}
        />
      )}
      {hasHero && (
        <section className="relative overflow-hidden">
          {page.hero_image_url && (
            <>
              <div
                className="absolute inset-0 bg-cover bg-center opacity-40 mix-blend-luminosity"
                style={{ backgroundImage: `url(${page.hero_image_url})` }}
                aria-hidden="true"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-slate-950/70 via-slate-950/30 to-slate-950" />
            </>
          )}
          <div className="relative max-w-5xl mx-auto px-6 lg:px-10 py-24">
            {page.hero_eyebrow && (
              <div className="text-xs uppercase tracking-[0.22em] text-atc-green mb-5">
                {page.hero_eyebrow}
              </div>
            )}
            <ReflectedHeading
              as="h1"
              className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[0.95] chrome-gradient-text"
            >
              {page.hero_title || page.title}
            </ReflectedHeading>
            {page.hero_subtitle && (
              <p className="mt-10 text-lg text-slate-300 max-w-2xl">{page.hero_subtitle}</p>
            )}
          </div>
        </section>
      )}

      <section className="water-section max-w-3xl mx-auto px-6 lg:px-10 py-20" data-testid="custom-page-content">
        {!hasHero && (
          <ReflectedHeading
            as="h1"
            className="font-display font-black text-4xl sm:text-5xl tracking-tight leading-tight chrome-gradient-text mb-12"
          >
            {page.title}
          </ReflectedHeading>
        )}
        <EditableHTML
          html={page.content_html}
          className="text-slate-200 text-base sm:text-lg leading-relaxed prose-styled"
        />
      </section>

      <Footer content={homeContent} />
    </main>
  );
};

export default CustomPage;
